import React, { useState, useRef, useEffect } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  FolderOpen,
  Image,
  BarChart3,
  Settings,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  LogOut,
  User,
  MoreHorizontal,
  Loader2,
  Shield,
  Users,
  Terminal,
  GitBranch,
  Wallet,
  CreditCard,
  TrendingUp,
  Lightbulb,
  ShieldAlert,
  Mail,
  ExternalLink,
  BookOpen,
  MessageCircle,
  MessagesSquare,
  Palette,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useBackgroundJobStore } from '../stores/backgroundJobStore'
import { GENEMO_APPS, appMarkUrl } from '../lib/brandAssets'

const CURRENT_APP_KEY = 'short'
const ADMIN_FOLD_KEY = 'gm-nav-fold:admin'
const OTHER_APPS = GENEMO_APPS.filter(app => app.key !== CURRENT_APP_KEY)

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  mobileOpen?: boolean
  onMobileClose?: () => void
}

interface NavItem {
  id: string
  label: string
  icon: React.ReactNode
  path: string
  external?: boolean
}

const adminItems: NavItem[] = [
  { id: 'admin-projects', label: 'プロジェクト管理', icon: <FolderOpen className="w-4 h-4" />, path: '/admin/projects' },
  { id: 'admin-stats',    label: '統計',             icon: <BarChart3 className="w-4 h-4" />,  path: '/admin/stats' },
  { id: 'admin-revenue',  label: '収益',             icon: <TrendingUp className="w-4 h-4" />, path: '/admin/revenue' },
  { id: 'admin-logs',     label: 'ログ調査',         icon: <Shield className="w-4 h-4" />,     path: '/admin/logs' },
  { id: 'admin-users',    label: 'ユーザー管理',     icon: <Users className="w-4 h-4" />,      path: '/admin/users' },
  { id: 'admin-ip',      label: 'IP重複管理',       icon: <ShieldAlert className="w-4 h-4" />, path: '/admin/ip-duplicates' },
  { id: 'admin-syslogs',  label: 'システムログ',     icon: <Terminal className="w-4 h-4" />,   path: '/admin/system-logs' },
  { id: 'admin-media',    label: 'メディア管理',     icon: <Image className="w-4 h-4" />,      path: '/admin/media' },
  { id: 'admin-costs',    label: 'コスト管理',       icon: <BarChart3 className="w-4 h-4" />,  path: '/admin/costs' },
  { id: 'admin-billing',  label: '課金管理',         icon: <CreditCard className="w-4 h-4" />, path: '/admin/billing' },
  { id: 'admin-tips',    label: 'チップス管理',     icon: <Lightbulb className="w-4 h-4" />, path: '/admin/tips' },
  { id: 'admin-taste-presets', label: 'テイスト管理', icon: <Palette className="w-4 h-4" />, path: '/admin/taste-presets' },
  { id: 'admin-decor-parts', label: '装飾パーツ管理', icon: <Sparkles className="w-4 h-4" />, path: '/admin/decor-parts' },
  { id: 'admin-prompts', label: 'プロンプト管理',   icon: <Settings className="w-4 h-4" />, path: '/admin/prompts' },
  { id: 'admin-global',  label: 'グローバル設定',   icon: <Settings className="w-4 h-4" />, path: '/admin/global-settings' },
  { id: 'admin-chatbot', label: 'チャットボット設定', icon: <MessageCircle className="w-4 h-4" />, path: '/admin/chatbot-settings' },
  { id: 'admin-chatbot-logs', label: 'チャットログ', icon: <MessagesSquare className="w-4 h-4" />, path: '/admin/chatbot-logs' },
  { id: 'admin-pipeline', label: 'パイプラインフロー', icon: <GitBranch className="w-4 h-4" />, path: '/admin/pipeline-flow' },
  { id: 'admin-mailer',  label: 'Genemo Mailer',    icon: <Mail className="w-4 h-4" />,     path: 'https://mailer.genemo.jp', external: true },
]

const navItems: NavItem[] = [
  {
    id: 'projects',
    label: 'プロジェクト',
    icon: <FolderOpen className="w-4 h-4" />,
    path: '/',
  },
  {
    id: 'media',
    label: 'メディア',
    icon: <Image className="w-4 h-4" />,
    path: '/media',
  },
  {
    id: 'history',
    label: '履歴',
    icon: <Clock className="w-4 h-4" />,
    path: '/history',
  },
  {
    id: 'settings',
    label: '設定',
    icon: <Settings className="w-4 h-4" />,
    path: '/settings',
  },
  {
    id: 'account',
    label: 'アカウント',
    icon: <Wallet className="w-4 h-4" />,
    path: '/account',
  },
  {
    id: 'manual',
    label: '使い方',
    icon: <BookOpen className="w-4 h-4" />,
    path: '/manual',
  },
]

/** md(768px)以上か。畳み表示はデスクトップだけに適用するために使う */
function useIsDesktop(): boolean {
  const query = '(min-width: 768px)'
  const [matches, setMatches] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : true))
  useEffect(() => {
    const mq = window.matchMedia(query)
    const handler = () => setMatches(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return matches
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed: collapsedPref, onToggle, mobileOpen = false, onMobileClose }) => {
  // 保存済みの「畳み」設定はデスクトップだけに効かせる（モバイルのドロワーは常に展開表示。
  // 畳み状態のままモバイル幅になると、展開ボタンが無く管理者ナビに入れなくなるため）
  const isDesktop = useIsDesktop()
  const collapsed = collapsedPref && isDesktop
  const onMobileCloseRef = useRef(onMobileClose)
  onMobileCloseRef.current = onMobileClose
  const location = useLocation()
  const { user, logout } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  // 管理者の畳み: 最初は閉じる。いまの画面が管理者の中なら開く。開け閉めは localStorage に覚える
  const [adminExpanded, setAdminExpanded] = useState(() => {
    if (location.pathname.startsWith('/admin')) return true
    try { return localStorage.getItem(ADMIN_FOLD_KEY) === '1' } catch { return false }
  })
  const toggleAdmin = () => setAdminExpanded(v => {
    try { localStorage.setItem(ADMIN_FOLD_KEY, v ? '0' : '1') } catch { /* 保存できなくても動く */ }
    return !v
  })
  const userMenuRef = useRef<HTMLDivElement>(null)
  const asideRef = useRef<HTMLElement>(null)
  const backgroundJobs = useBackgroundJobStore(s => s.jobs)
  const activeJobs = Object.values(backgroundJobs).filter(j => j.status === 'processing')

  const handleLogout = async () => {
    await logout()
    setShowUserMenu(false)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showUserMenu])

  // モバイルのドロワー: 開いたら中へフォーカスを移し、Escape で閉じ、閉じたら開いたボタンへフォーカスを戻す。
  // 背景(メイン画面)は AppLayout 側で inert にして、Tab が背後へ抜けないようにしている。
  // onMobileClose は ref 経由で参照する（親の再描画で effect が走り直してフォーカスが先頭に戻るのを防ぐ）
  useEffect(() => {
    if (!mobileOpen) return
    const opener = document.querySelector<HTMLElement>('[aria-controls="app-sidebar"]')
    asideRef.current?.querySelector<HTMLElement>('a, button')?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onMobileCloseRef.current?.() }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      opener?.focus()
    }
  }, [mobileOpen])

  // ナビ行: パール(gm-nav-item)。現在地は aria-current で白い板＋影。畳み時はアイコンだけ
  const navClass = (_isActive: boolean, compact = false) =>
    clsx('gm-nav-item', compact && '!min-h-[38px]', collapsed && 'justify-center !px-0 after:!hidden')

  return (
    <>
      {/* Mobile backdrop（トップバーは隠さない） */}
      {mobileOpen && (
        <div
          className="fixed inset-x-0 bottom-0 top-[var(--gm-shell-topbar-h)] z-40 bg-ink/40 md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}
      <aside
        id="app-sidebar"
        ref={asideRef}
        aria-label="メインメニュー"
        className={clsx(
          'sidebar-container gm-sidebar',
          // デスクトップ: 画面端と上のバーから gap だけ離して浮かせる。モバイル: 左から出すドロワー（角丸は右だけ）
          'fixed z-50 bottom-0 top-[var(--gm-shell-topbar-h)] max-md:rounded-l-none md:bottom-[var(--gm-shell-gap)] md:left-[var(--gm-shell-gap)]',
          'transition-transform duration-200 motion-reduce:transition-none md:transition-none',
          collapsed && 'md:!w-16 md:!px-2',
          // モバイル: 閉じている間は画面外＋invisible（キーボードフォーカスも届かない）
          mobileOpen ? 'translate-x-0' : '-translate-x-full max-md:invisible',
          'md:translate-x-0'
        )}
      >
      {/* New Project Button */}
      <NavLink
        to="/new"
        onClick={onMobileClose}
        className={clsx('gm-btn gm-btn-secondary gm-btn-pill gm-side-new', collapsed && '!px-0')}
        title={collapsed ? '新規プロジェクト' : undefined}
        aria-label="新規プロジェクト"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {!collapsed && <span>新規プロジェクト</span>}
      </NavLink>

      {/* Navigation */}
      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
      <nav className="grid gap-0.5" aria-label="画面">
        {navItems.map((item) => {
          const isActive =
            item.external
              ? false
              : item.path === '/'
                ? location.pathname === '/' || location.pathname.startsWith('/project')
                : location.pathname.startsWith(item.path)

          if (item.external) {
            // 外部リンク（http://... も内部公開ページも別タブで開く）
            return (
              <a
                key={item.id}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onMobileClose}
                className={navClass(false)}
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
              >
                {item.icon}
                {!collapsed && <span className="flex-1 font-medium">{item.label}</span>}
                {!collapsed && <ExternalLink className="h-3 w-3 opacity-60" aria-hidden="true" />}
              </a>
            )
          }

          return (
            <NavLink
              key={item.id}
              to={item.path}
              onClick={onMobileClose}
              className={navClass(isActive)}
              aria-current={isActive ? 'page' : undefined}
              title={collapsed ? item.label : undefined}
              aria-label={collapsed ? item.label : undefined}
            >
              {item.icon}
              {!collapsed && <span className="font-medium">{item.label}</span>}
            </NavLink>
          )
        })}
      </nav>

      {/* Admin: 折りたたみセクション */}
      {user?.isSystemAdmin && (
        <div className="mt-1">
          {collapsed ? (
            // collapsed時: Shieldアイコンで最初の管理者ページへ
            <NavLink
              to="/admin/projects"
              className={navClass(location.pathname.startsWith('/admin'), true)}
              title="管理者メニュー"
              aria-label="管理者メニュー"
            >
              <Shield className="h-4 w-4" aria-hidden="true" />
            </NavLink>
          ) : (
            <div className="gm-nav-fold">
              <button
                type="button"
                onClick={toggleAdmin}
                aria-expanded={adminExpanded}
                aria-controls="nav-fold-admin"
                className="gm-nav-fold-btn"
              >
                <Shield aria-hidden="true" />
                <span>管理者</span>
                <span className="gm-nav-fold-count">{adminItems.length}</span>
                <ChevronDown className="gm-nav-fold-chev" aria-hidden="true" />
              </button>
              <div className="gm-nav-fold-body" id="nav-fold-admin">
                <div className="gm-nav-fold-inner">{adminItems.map(item => {
                  if (item.external) {
                    return (
                      <a
                        key={item.id}
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={navClass(false, true)}
                      >
                        {item.icon}
                        <span className="flex-1 font-medium">{item.label}</span>
                        <ExternalLink className="h-3 w-3 opacity-60" aria-hidden="true" />
                      </a>
                    )
                  }
                  const isActive = location.pathname.startsWith(item.path)
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      className={navClass(isActive, true)}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  )
                })}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Background Tasks（AI 作業中: gm-ai-ring ＋「生成中」の文字） */}
      {activeJobs.length > 0 && (
        <div className={clsx('border-t border-line', collapsed ? 'px-1 py-2' : 'px-3 py-2')} aria-live="polite">
          {!collapsed && (
            <p className="mb-1.5 px-1 text-xs font-bold text-ink-muted">生成中</p>
          )}
          <div className="space-y-1.5">
            {activeJobs.map(job => (
              <Link
                key={job.projectId}
                to={job.type === 'analysis'
                  ? `/project/${job.projectId}/analyzing`
                  : `/project/${job.projectId}/storyboard`}
                title={collapsed ? `${job.projectName} ${job.progress}%` : undefined}
                aria-label={collapsed ? `${job.projectName} 生成中 ${job.progress}%` : undefined}
                className={clsx(
                  'block rounded-ctl transition-colors hover:bg-surface-2',
                  collapsed ? 'flex items-center justify-center p-1.5' : 'p-2'
                )}
              >
                {collapsed ? (
                  <span className="gm-ai-ring" aria-hidden="true" />
                ) : (
                  <>
                    <div className="mb-1 flex items-center gap-1.5">
                      <span className="gm-ai-ring shrink-0" aria-hidden="true" />
                      <span className="flex-1 truncate text-xs font-bold text-ink">
                        {job.projectName}
                      </span>
                      <span className="gm-num shrink-0 text-xs font-bold text-primary">
                        {job.progress}%
                      </span>
                    </div>
                    <div className="gm-meter" role="progressbar" aria-valuenow={job.progress} aria-valuemin={0} aria-valuemax={100} aria-label={`${job.projectName} の進捗`}>
                      <span style={{ width: `${job.progress}%` }} />
                    </div>
                    <p className="mt-0.5 truncate text-xs text-ink-muted">生成中{job.message ? `：${job.message}` : ''}</p>
                  </>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* アプリ（Short 以外の 6 つ。マークだけ横 1 行。畳み時は縦に並べる） */}
      {!collapsed ? (
        <>
          <div className="gm-nav-heading">アプリ</div>
          <nav className="gm-nav-marks" aria-label="Genemo のほかのアプリ">
            {OTHER_APPS.map(app => (
              <a key={app.key} href={app.url} className="gm-nav-mark" data-name={app.label} aria-label={app.name}>
                <img src={appMarkUrl(app)} alt="" />
              </a>
            ))}
          </nav>
        </>
      ) : (
        <nav className="mt-3 grid gap-0.5" aria-label="Genemo のほかのアプリ">
          {OTHER_APPS.map(app => (
            <a key={app.key} href={app.url} className="gm-nav-item gm-nav-app justify-center !px-0" title={app.name}>
              <img src={appMarkUrl(app)} alt={app.label} />
            </a>
          ))}
        </nav>
      )}
      </div>

      {/* サイドバーの畳み/展開（デスクトップのみ。編集画面で作業領域を広げる用） */}
      <div className="hidden md:block">
        <button
          type="button"
          onClick={onToggle}
          className={clsx('gm-nav-item !min-h-[38px]', collapsed && 'justify-center !px-0')}
          aria-label={collapsed ? 'サイドバーを広げる' : 'サイドバーを畳む'}
          title={collapsed ? 'サイドバーを広げる' : 'サイドバーを畳む'}
        >
          {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
          {!collapsed && <span>サイドバーを畳む</span>}
        </button>
      </div>

      {/* アカウント：メール＋「⋯」で 利用規約・特商法表示・ログアウト */}
      <div ref={userMenuRef} className="gm-side-account">
        {showUserMenu && (
          <div role="menu" className="gm-menu">
            {user?.email && <div className="gm-menu-head">{user.email}</div>}
            <button type="button" role="menuitem" onClick={handleLogout} className="gm-menu-item gm-menu-item-danger">
              <LogOut aria-hidden="true" /> ログアウト
            </button>
          </div>
        )}
        <span className="gm-side-account-icon"><User size={16} aria-hidden="true" /></span>
        {!collapsed && <span className="gm-side-account-mail" title={user?.email}>{user?.email ?? 'My Account'}</span>}
        {!collapsed && (
          <button
            type="button"
            className="gm-icon-btn"
            onClick={() => setShowUserMenu(v => !v)}
            aria-label="アカウントメニューを開く"
            aria-expanded={showUserMenu}
            aria-haspopup="menu"
          >
            <MoreHorizontal size={18} aria-hidden="true" />
          </button>
        )}
        {collapsed && (
          <button type="button" className="gm-icon-btn absolute inset-0 !h-full !w-full opacity-0" onClick={handleLogout} title="ログアウト" aria-label="ログアウト" />
        )}
      </div>
      </aside>
    </>
  )
}

export default Sidebar
